import { spaceUsersListUrl } from "@Services/dataManager";
import { renderUserTag } from "@CubeComponents/html/userTagDom";
import { useRef, useMemo } from "react";

import SlickGrid1 from "@CubeComponents/html/SlickGrid1";
import { typedColumns, type GridRowBase } from "@CubeComponents/html/SlickGrid/types";
// This file renders `SlickGrid1` but builds its columns with `typedColumns` from the older
// `SlickGrid` module, whose validation brand is a distinct type. SlickGrid1's own brand is
// imported so the JSX boundary can assert across the two, replacing an untyped escape hatch.
import type { SlickGrid1Ref, ValidatedColumnDefinition } from "@CubeComponents/html/SlickGrid1";
import { useTranslation } from "@i18n";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";

/**
 * Row type for space user data. `userId` is required because the `getSpaceUsersList` aggregation
 * always projects it (`userId: "$users._id"`).
 */
export interface SpaceUserRow extends GridRowBase {
  userName?: string;
  fullName?: string;
  organisationName?: string;
  userId: ObjectIdString;
}

/** Shape of the SlickGrid `onSelectedRowIdsChanged` event this grid actually receives. */
type SelectedRowIdsChangedEvent = Record<string, unknown> & {
  detail: { args: { rows: number[] } };
  cancel?: boolean;
};

interface UsersListInSpaceProps {
  /** Plain `string`: callers hand this down from route params / component props, unvalidated. */
  spaceId?: string;
  selectUsers: (users: SpaceUserRow[]) => void;
  disabledUserIds?: string[];
  /**
   * DEAD PROP: passed by `AddUserModal` but read by neither this component nor the grid it renders.
   * Declared so the call site keeps type-checking; dropping it from the caller would be a
   * behaviour-neutral cleanup.
   */
  allowMultiSelection?: boolean;
}

function UsersListInSpace({ spaceId, selectUsers, disabledUserIds = [] }: UsersListInSpaceProps) {
  const dataGrid = useRef<SlickGrid1Ref<SpaceUserRow> | null>(null);
  const { t } = useTranslation();

  const spaceUsersQuery = useMemo(
    () => ({
      spaceId,
      params: {
        spaceId,
      },
    }),
    [spaceId],
  );
  const selectionEvents = (args: SelectedRowIdsChangedEvent) => {
    // KNOWN BUG, preserved verbatim: `dataGrid.current` is dereferenced without a guard, so an
    // event delivered before/after mount would throw. Erased assertions; adding `?.` would change
    // behaviour.
    const rowData = dataGrid.current!.slickGrid.getDataItem(args.detail.args.rows[0]!);
    if (rowData) {
      if (disabledUserIds.includes(rowData.userId)) {
        args.cancel = true;
        return;
      }

      const selectedRecords = dataGrid.current!.dataView.getAllSelectedItems();
      selectUsers(selectedRecords);
    }
  };

  const userNameTemplate = (
    _row: number,
    _cell: number,
    _value: string | undefined,
    _columnDef: unknown,
    dataContext: SpaceUserRow,
  ) => {
    const container = document.createElement("div");
    const { userId } = dataContext;

    renderUserTag(container, { id: userId });
    return container;
  };

  const columnDefinitions = useMemo(
    () =>
      typedColumns<SpaceUserRow>([
        {
          field: "userName",
          id: "fullName",
          name: t("Users"),
          _valueGetter: (params) => params.fullName,
          formatter: userNameTemplate,
          filterType: "Text",
        },
        {
          field: "organisationName",
          id: "organisation",
          name: t("Organization"),
          filterType: "Text",
          _valueGetter: (params) => params.organisationName,
        },
      ]),
    [],
  );

  if (!spaceId) {
    return null;
  }

  return (
    <div className="h-[18.75rem]">
      <SlickGrid1
        ref={dataGrid}
        remoteDataSource={spaceUsersListUrl}
        query={spaceUsersQuery}
        columnDefinitions={columnDefinitions as typeof columnDefinitions & ValidatedColumnDefinition<SpaceUserRow>[]}
        disabledIds={disabledUserIds}
        enableFilteringOption
        // The grid declares this callback's arg as a bare index-signature record, so the concrete
        // event shape read above has to be re-widened here. Erased assertion.
        onSelectedRowIdsChanged={selectionEvents as (args: Record<string, unknown>) => void}
      />
    </div>
  );
}

export default UsersListInSpace;
